import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Entity, EntityCreateRequest, EntityUpdateRequest } from '@cortex/domain';

import { cortexApi } from '@/services/cortexApi';
import { resolveApiError } from '@/services/errorMessages';
import { useToast } from '@/components/ui/Toast';
import { AdminMasterDetail } from './AdminMasterDetail';
import { EntityList } from './EntityList';
import { EntityDetail, type EntityTypeOption } from './EntityDetail';

/**
 * Controller for the Entities tab of the Admin Module.
 *
 * Server state (the entity list) lives in TanStack Query under the
 * `['admin','entities']` key. Mutations (create / update) invalidate
 * that key on success, so the list re-fetches and reflects the change
 * only after the write confirms — the pessimistic update the
 * forms-&-data rule requires for status-bearing records.
 *
 * Local UI state (which row is selected, create vs edit mode, the
 * mobile drill-down flag, the inline form error) stays in component
 * state. Network errors become branded toasts via `resolveApiError`.
 *
 * Entity-type options come from the v1 closed taxonomy in
 * `docs/USER-ENTITY-MODEL.md` §2. When the runtime-configurable type
 * registry lands, swap `V1_ENTITY_TYPES` for a query against
 * /admin/entity-types.
 */

const ENTITIES_KEY = ['admin', 'entities'] as const;

const V1_ENTITY_TYPES: readonly EntityTypeOption[] = [
  { slug: 'insurer', displayName: 'Insurer' },
  { slug: 'brokerage', displayName: 'Brokerage' },
  { slug: 'client', displayName: 'Client' },
  { slug: 'assistance-company', displayName: 'Assistance Company' },
  { slug: 'reinsurer', displayName: 'Reinsurer' },
  { slug: 'loss-adjuster', displayName: 'Loss Adjuster' },
  { slug: 'tpa', displayName: 'TPA' },
];

type DetailMode = 'idle' | 'create' | 'edit';

export function EntitiesTab() {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [mode, setMode] = useState<DetailMode>('idle');
  const [formError, setFormError] = useState<string | null>(null);
  const [mobileShowDetail, setMobileShowDetail] = useState(false);

  const entitiesQuery = useQuery({
    queryKey: ENTITIES_KEY,
    queryFn: () => cortexApi.entities.list(),
  });

  const entities = useMemo<readonly Entity[]>(
    () => entitiesQuery.data?.entities ?? [],
    [entitiesQuery.data],
  );

  const listError = entitiesQuery.isError
    ? formatError(resolveApiError(entitiesQuery.error))
    : null;

  const selectedEntity = useMemo<Entity | null>(
    () => entities.find((e) => e.id === selectedEntityId) ?? null,
    [entities, selectedEntityId],
  );

  const createMutation = useMutation({
    mutationFn: (input: EntityCreateRequest) => cortexApi.entities.create(input),
    onSuccess: async (response) => {
      setFormError(null);
      showToast(`Created ${response.entity.name}.`, 'success');
      await queryClient.invalidateQueries({ queryKey: ENTITIES_KEY });
      setSelectedEntityId(response.entity.id);
      setMode('edit');
    },
    onError: (err) => {
      const resolved = resolveApiError(err);
      setFormError(formatError(resolved));
      showToast(formatError(resolved), resolved.tone);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: EntityUpdateRequest }) =>
      cortexApi.entities.update(id, input),
    onSuccess: async (response) => {
      setFormError(null);
      showToast(`Saved ${response.entity.name}.`, 'success');
      await queryClient.invalidateQueries({ queryKey: ENTITIES_KEY });
      setSelectedEntityId(response.entity.id);
    },
    onError: (err) => {
      const resolved = resolveApiError(err);
      setFormError(formatError(resolved));
      showToast(formatError(resolved), resolved.tone);
    },
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const handleSelect = useCallback((id: string): void => {
    setSelectedEntityId(id);
    setMode('edit');
    setFormError(null);
    setMobileShowDetail(true);
  }, []);

  const handleCreateNew = useCallback((): void => {
    setSelectedEntityId(null);
    setMode('create');
    setFormError(null);
    setMobileShowDetail(true);
  }, []);

  const handleCancel = useCallback((): void => {
    setMode('idle');
    setSelectedEntityId(null);
    setFormError(null);
    setMobileShowDetail(false);
  }, []);

  const handleMobileBack = useCallback((): void => setMobileShowDetail(false), []);

  const handleCreate = useCallback(
    async (input: EntityCreateRequest): Promise<void> => {
      setFormError(null);
      // Errors surface via the mutation's onError handler; swallow the
      // rejection here so the form's await settles either way.
      await createMutation.mutateAsync(input).catch(() => undefined);
    },
    [createMutation],
  );

  const handleUpdate = useCallback(
    async (id: string, input: EntityUpdateRequest): Promise<void> => {
      setFormError(null);
      await updateMutation.mutateAsync({ id, input }).catch(() => undefined);
    },
    [updateMutation],
  );

  return (
    <AdminMasterDetail
      title="Entity Management"
      subtitle="Brokerages, insurers, and other counterparties known to CORTEX."
      mobileShowDetail={mobileShowDetail}
      onMobileBack={handleMobileBack}
      list={
        <EntityList
          entities={entities}
          selectedEntityId={selectedEntityId}
          isLoading={entitiesQuery.isLoading}
          errorMessage={listError}
          onSelect={handleSelect}
          onCreateNew={handleCreateNew}
        />
      }
      detail={
        <EntityDetail
          mode={mode}
          entity={selectedEntity}
          availableTypes={V1_ENTITY_TYPES}
          isSaving={isSaving}
          errorMessage={formError}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          onCancel={handleCancel}
        />
      }
    />
  );
}

const formatError = (resolved: { readonly title: string; readonly message: string }): string =>
  `${resolved.title}: ${resolved.message}`;
